import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { credentialsService } from '../../services/credentialsService';

// Async thunks
export const fetchCredentials = createAsyncThunk(
  'credentials/fetchCredentials',
  async (_, { rejectWithValue }) => {
    try {
      const response = await credentialsService.listCredentials();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addCredential = createAsyncThunk(
  'credentials/addCredential',
  async (credentialData, { rejectWithValue }) => {
    try {
      const response = await credentialsService.addCredential(credentialData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateCredential = createAsyncThunk(
  'credentials/updateCredential',
  async ({ credentialId, updates }, { rejectWithValue }) => {
    try {
      const response = await credentialsService.updateCredential(credentialId, updates);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteCredential = createAsyncThunk(
  'credentials/deleteCredential',
  async (credentialId, { rejectWithValue }) => {
    try {
      await credentialsService.deleteCredential(credentialId);
      return credentialId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchAuditLog = createAsyncThunk(
  'credentials/fetchAuditLog',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await credentialsService.getAuditLog(filters);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const credentialsSlice = createSlice({
  name: 'credentials',
  initialState: {
    items: [],
    auditLog: [],
    auditLogPagination: {
      page: 1,
      perPage: 50,
      total: 0
    },
    loading: false,
    error: null
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch credentials
      .addCase(fetchCredentials.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCredentials.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchCredentials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add credential
      .addCase(addCredential.pending, (state) => {
        state.loading = true;
      })
      .addCase(addCredential.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
      })
      .addCase(addCredential.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update credential
      .addCase(updateCredential.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCredential.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(cred => cred.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateCredential.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete credential
      .addCase(deleteCredential.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCredential.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(cred => cred.id !== action.payload);
      })
      .addCase(deleteCredential.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch audit log
      .addCase(fetchAuditLog.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAuditLog.fulfilled, (state, action) => {
        state.loading = false;
        state.auditLog = action.payload.data;
        state.auditLogPagination = {
          page: action.payload.page,
          perPage: action.payload.per_page,
          total: action.payload.total
        };
      })
      .addCase(fetchAuditLog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError } = credentialsSlice.actions;
export default credentialsSlice.reducer;
